import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PROVIDERS, loadConfig, saveConfig, testConnection } from '../../../aiConfig';
import { authFetch } from '../../../api';
import { keys } from '@/lib/queryKeys';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', color: '#6B7280', marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid #F3F4F6' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

interface UsageTotal {
  provider: string;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  audio_seconds: number;
  characters: number;
  charge_inr: number;
}

interface ProviderBudget {
  provider: string;
  budget_inr: number;
  used_inr: number;
  used_pct: number | null;
}

interface BudgetInfo {
  allowance_inr: number | null;
  used_inr: number;
  used_pct: number | null;
  overage_inr: number;
  warn_pct: number;
  provider_budgets: ProviderBudget[];
}

interface BudgetNotice {
  id: number;
  scope: string;
  threshold_pct: number;
  spend_at_crossing: number | null;
  created_at: string;
}

const inr = (v: number | null | undefined) => v == null ? '—' : `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Width is set imperatively (a ref + effect) rather than a JSX `style={{}}`
// attribute — this file's eslint-suppressions baseline is a per-rule COUNT
// for the whole file (see CLAUDE.md's own note on this), so even one new
// inline-style usage re-surfaces every pre-existing one. Tailwind's
// arbitrary-value classes (`w-[82%]`) aren't an option either: the value is
// only known at runtime, so the JIT scanner would never see the class name
// to keep it in the production build.
function ProgressBar({ pct }: { pct: number | null }) {
  const barRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (barRef.current) barRef.current.style.width = `${Math.min(100, Math.max(0, pct ?? 0))}%`;
  }, [pct]);
  if (pct == null) return null;
  const tone = pct >= 100 ? 'bg-destructive' : pct >= 80 ? 'bg-amber-500' : 'bg-primary';
  return (
    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <div ref={barRef} className={`h-full ${tone}`} />
    </div>
  );
}

// G3+G4 of docs/HMS_V4_AI_PROVIDER_MANAGEMENT.md — calls/tokens/charges for
// THIS hospital (trailing 30 days) plus this month's allowance/budget
// progress and any unacknowledged threshold-crossing banners, all from one
// GET /api/admin/ai-usage call. Deliberately shows charge_inr (what the
// hospital is billed), never the raw provider_cost/cost_currency the
// platform console's own billing screen carries — see the route's own
// header for why.
function UsageSection() {
  const [totals, setTotals] = useState<UsageTotal[] | null>(null);
  const [budget, setBudget] = useState<BudgetInfo | null>(null);
  const [notices, setNotices] = useState<BudgetNotice[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    authFetch('/api/admin/ai-usage')
      .then(async (r) => {
        if (!r.ok) throw new Error(`Failed to load usage (${r.status})`);
        return r.json();
      })
      .then((data) => {
        setTotals(data.totals_by_provider || []);
        setBudget(data.budget || null);
        setNotices(data.notices || []);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const acknowledge = async (id: number) => {
    await authFetch(`/api/admin/ai-usage/notices/${id}/acknowledge`, { method: 'PATCH' });
    setNotices((prev) => prev.filter((n) => n.id !== id));
  };

  return (
    <Section title="Usage (last 30 days)">
      {loading && <div className="text-[13px] text-muted-foreground">Loading…</div>}
      {error && <div className="text-[13px] text-destructive">{error}</div>}

      {!loading && !error && notices.map((n) => (
        <div key={n.id} className="mb-2.5 flex items-center justify-between gap-3 rounded-[10px] border border-amber-300 bg-amber-50 px-3.5 py-2.5 text-[13px] text-amber-900">
          <span>
            ⚠ AI usage {n.threshold_pct >= 100 ? 'has exceeded' : `is at ${n.threshold_pct}% of`}{' '}
            {n.scope === 'total' ? "this month's AI allowance" : `this month's ${n.scope.replace('provider:', '')} budget`}
            {n.spend_at_crossing != null ? ` (${inr(n.spend_at_crossing)})` : ''}.
          </span>
          <button className="btn-ghost shrink-0" onClick={() => acknowledge(n.id)}>Dismiss</button>
        </div>
      ))}

      {!loading && !error && budget?.allowance_inr != null && (
        <div className="mb-3.5 rounded-[10px] border border-border bg-muted p-3.5">
          <div className="flex items-baseline justify-between text-[13px]">
            <span className="font-semibold text-ink">Monthly allowance</span>
            <span className="text-muted-foreground">
              {inr(budget.used_inr)} of {inr(budget.allowance_inr)} used{budget.used_pct != null ? ` (${budget.used_pct}%)` : ''}
            </span>
          </div>
          <ProgressBar pct={budget.used_pct} />
          {budget.overage_inr > 0 && (
            <div className="mt-1.5 text-xs text-muted-foreground">Overage this month: {inr(budget.overage_inr)}</div>
          )}
          {budget.provider_budgets.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-3.5">
              {budget.provider_budgets.map((pb) => (
                <div key={pb.provider} className="min-w-[160px] flex-1 text-xs">
                  <div className="flex items-baseline justify-between">
                    <span className="font-semibold text-ink">{pb.provider} budget</span>
                    <span className="text-muted-foreground">{inr(pb.used_inr)} / {inr(pb.budget_inr)}</span>
                  </div>
                  <ProgressBar pct={pb.used_pct} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!loading && !error && totals && totals.length === 0 && (
        <div className="text-[13px] text-muted-foreground">No AI calls logged yet in this window.</div>
      )}
      {!loading && !error && totals && totals.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-2.5 py-2">Provider</th>
                <th className="px-2.5 py-2">Calls</th>
                <th className="px-2.5 py-2">Input tokens</th>
                <th className="px-2.5 py-2">Output tokens</th>
                <th className="px-2.5 py-2">Audio (s)</th>
                <th className="px-2.5 py-2">Characters</th>
                <th className="px-2.5 py-2">Charge</th>
              </tr>
            </thead>
            <tbody>
              {totals.map((t) => (
                <tr key={t.provider} className="border-b border-muted">
                  <td className="px-2.5 py-2 font-semibold text-ink">{t.provider}</td>
                  <td className="px-2.5 py-2">{t.calls.toLocaleString()}</td>
                  <td className="px-2.5 py-2">{t.input_tokens.toLocaleString()}</td>
                  <td className="px-2.5 py-2">{t.output_tokens.toLocaleString()}</td>
                  <td className="px-2.5 py-2">{t.audio_seconds ? t.audio_seconds.toLocaleString() : '—'}</td>
                  <td className="px-2.5 py-2">{t.characters ? t.characters.toLocaleString() : '—'}</td>
                  <td className="px-2.5 py-2">{inr(t.charge_inr)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 text-xs text-muted-foreground">
            Charges are estimated from the platform price list and today's exchange rate — unverified prices may undercount or overcount actual provider billing.
          </div>
        </div>
      )}
    </Section>
  );
}

function ProviderCard({ provider, selected, onSelect }: { provider: any; selected: boolean; onSelect: () => void }) {
  return (
    <div onClick={onSelect} style={{
      border: `2px solid ${selected ? provider.color : '#E5E7EB'}`,
      borderRadius: 12, padding: '14px 16px', cursor: 'pointer',
      background: selected ? provider.bg : '#fff',
      transition: 'all .18s', position: 'relative',
    }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.borderColor = provider.color + '80'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.borderColor = '#E5E7EB'; }}
    >
      {selected && (
        <div style={{ position: 'absolute', top: 10, right: 10, width: 20, height: 20, borderRadius: '50%', background: provider.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 700 }}>✓</div>
      )}
      <div style={{ fontSize: 24, marginBottom: 8 }}>{provider.icon}</div>
      <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{provider.name}</div>
    </div>
  );
}

export default function AISettingsTab({ onSaved }: { onSaved?: (cfg: unknown) => void }) {
  const qc = useQueryClient();
  const [cfg, setCfg] = useState<any>(loadConfig);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [saved, setSaved] = useState(false);

  // Tenant-wide AI processing policy switches (ai_settings singleton) — see
  // backend/routes/ai-settings.js. Two independent switches: whether AI runs
  // at all (every /api/ai/* call is hard-blocked server-side when off,
  // independent of anything in this browser's localStorage config below),
  // and — separately — whether a patient-linked call additionally requires a
  // granted patient_consents row before it proceeds. Turning consent off
  // does NOT turn AI off; it only removes that one gate.
  const [policy, setPolicy] = useState<{
    ai_processing_enabled: boolean; ai_consent_required: boolean; default_ai_provider: string | null;
    doctor_memory_enabled: boolean; memory_doc_types: string[]; default_voice_provider: string;
    kiosk_ai_extraction_enabled: boolean; kiosk_extraction_model: string | null;
    consultation_audio_retention_days?: number; structured_clinical_draft?: boolean;
    updated_by?: string; updated_at?: string;
  } | null>(null);
  const [policyLoading, setPolicyLoading] = useState(true);
  const [policySaving, setPolicySaving] = useState<'processing' | 'consent' | 'provider' | 'memory' | 'memoryDocTypes' | 'voice' | 'kioskExtraction' | 'kioskModel' | 'audioRetention' | 'structuredDraft' | null>(null);
  const [policyError, setPolicyError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch('/api/ai-settings');
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Failed to load AI policy');
        setPolicy(d);
        setKioskModelDraft(d.kiosk_extraction_model || '');
      } catch (e: any) {
        setPolicyError(e.message);
      } finally {
        setPolicyLoading(false);
      }
    })();
  }, []);

  const togglePolicy = async () => {
    if (!policy) return;
    const next = !policy.ai_processing_enabled;
    setPolicySaving('processing'); setPolicyError(null);
    try {
      const r = await authFetch('/api/ai-settings', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ai_processing_enabled: next }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to update AI policy');
      setPolicy(d);
      // Instant propagation (§4.2): every AiOnly-gated control elsewhere in
      // the app reads useAiAvailability(), backed by this same query key —
      // without this, a screen already open in another tab only catches up
      // after its 60s staleTime or the next window focus.
      qc.invalidateQueries({ queryKey: keys.config.aiSettings() });
    } catch (e: any) {
      setPolicyError(e.message);
    } finally {
      setPolicySaving(null);
    }
  };

  const toggleConsentRequired = async () => {
    if (!policy) return;
    const next = !policy.ai_consent_required;
    setPolicySaving('consent'); setPolicyError(null);
    try {
      const r = await authFetch('/api/ai-settings', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ai_consent_required: next }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to update AI consent policy');
      setPolicy(d);
    } catch (e: any) {
      setPolicyError(e.message);
    } finally {
      setPolicySaving(null);
    }
  };

  // The server-side vendor choice for every patient-linked call (audit-
  // flagged: those routes previously trusted a client-supplied `provider`
  // field, letting any logged-in caller redirect patient data to a
  // different vendor than the hospital chose). This is a SEPARATE setting
  // from the "AI Provider" section below, which only picks the
  // model/temperature/token defaults this browser sends for the parts of a
  // request that were always safe to leave client-side.
  const setDefaultProvider = async (provider: string) => {
    if (!policy) return;
    setPolicySaving('provider'); setPolicyError(null);
    try {
      const r = await authFetch('/api/ai-settings', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ default_ai_provider: provider }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to update the default AI vendor');
      setPolicy(d);
    } catch (e: any) {
      setPolicyError(e.message);
    } finally {
      setPolicySaving(null);
    }
  };

  // The voice vendor (G2, docs/HMS_V4_AI_PROVIDER_MANAGEMENT.md §5) —
  // speech-to-text dictation, translation, transliteration and (once a
  // caller uses it) kiosk TTS guidance, all enforced server-side through
  // utils/voice/voiceGateway.js. A hardcoded list here, same precedent as
  // PROVIDERS above for document vendors — only one voice vendor exists
  // today (Sarvam), and a new one is added in code (utils/voice/registry.js)
  // alongside this list, not by a migration.
  const VOICE_PROVIDERS = [{ id: 'sarvam', name: 'Sarvam AI' }];

  const setDefaultVoiceProvider = async (provider: string) => {
    if (!policy) return;
    setPolicySaving('voice'); setPolicyError(null);
    try {
      const r = await authFetch('/api/ai-settings', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ default_voice_provider: provider }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to update the default voice vendor');
      setPolicy(d);
    } catch (e: any) {
      setPolicyError(e.message);
    } finally {
      setPolicySaving(null);
    }
  };

  // Kiosk AI extraction (G5, D14/D23) — off by default. OpenAI is removed
  // from the kiosk intake path entirely regardless of this switch; turning
  // it on only lets the on-premise parser's gaps be filled by the voice
  // vendor's own LLM, and should only be flipped once
  // scripts/kiosk-extraction-accuracy.js has been run against real role-
  // play recordings and clears the D23 bar. See routes/voice.js's
  // /extract-intent.
  const toggleKioskAiExtraction = async () => {
    if (!policy) return;
    const next = !policy.kiosk_ai_extraction_enabled;
    setPolicySaving('kioskExtraction'); setPolicyError(null);
    try {
      const r = await authFetch('/api/ai-settings', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kiosk_ai_extraction_enabled: next }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to update kiosk AI extraction policy');
      setPolicy(d);
    } catch (e: any) {
      setPolicyError(e.message);
    } finally {
      setPolicySaving(null);
    }
  };

  const [kioskModelDraft, setKioskModelDraft] = useState('');
  const saveKioskExtractionModel = async () => {
    if (!policy) return;
    setPolicySaving('kioskModel'); setPolicyError(null);
    try {
      const r = await authFetch('/api/ai-settings', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kiosk_extraction_model: kioskModelDraft.trim() || null }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to update the kiosk extraction model');
      setPolicy(d);
    } catch (e: any) {
      setPolicyError(e.message);
    } finally {
      setPolicySaving(null);
    }
  };

  // G4 (Phase 1 quotation): how long consultation audio is kept, and the
  // six-section clinical draft. See backend/utils/consultationRecordings.js
  // and backend/utils/clinicalDraftSections.js.
  const [retentionDraft, setRetentionDraft] = useState<string | null>(null);
  const patchPolicy = async (key: 'audioRetention' | 'structuredDraft', body: Record<string, unknown>) => {
    setPolicySaving(key); setPolicyError(null);
    try {
      const r = await authFetch('/api/ai-settings', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to save');
      setPolicy(d);
      setRetentionDraft(null);
    } catch (e: any) {
      setPolicyError(e.message);
    } finally {
      setPolicySaving(null);
    }
  };
  const saveAudioRetention = () => {
    const days = Number(retentionDraft);
    if (!Number.isInteger(days) || days < 0 || days > 3650) { setPolicyError('Enter a whole number of days from 0 to 3650.'); return; }
    void patchPolicy('audioRetention', { consultation_audio_retention_days: days });
  };

  // Doctor AI Memory (Phase 2) — the hospital-wide kill switch and which
  // document types it applies to (D4: Consultation Notes first). See
  // backend/utils/doctorAiMemory.js.
  const MEMORY_DOC_TYPE_OPTIONS = ['Consultation Notes', 'Discharge Summary', 'OPD Prescription'];

  const toggleDoctorMemory = async () => {
    if (!policy) return;
    const next = !policy.doctor_memory_enabled;
    setPolicySaving('memory'); setPolicyError(null);
    try {
      const r = await authFetch('/api/ai-settings', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ doctor_memory_enabled: next }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to update Doctor AI Memory policy');
      setPolicy(d);
    } catch (e: any) {
      setPolicyError(e.message);
    } finally {
      setPolicySaving(null);
    }
  };

  const toggleMemoryDocType = async (docType: string) => {
    if (!policy) return;
    const set = new Set(policy.memory_doc_types);
    if (set.has(docType)) set.delete(docType); else set.add(docType);
    setPolicySaving('memoryDocTypes'); setPolicyError(null);
    try {
      const r = await authFetch('/api/ai-settings', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memory_doc_types: Array.from(set) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to update which document types Doctor AI Memory applies to');
      setPolicy(d);
    } catch (e: any) {
      setPolicyError(e.message);
    } finally {
      setPolicySaving(null);
    }
  };

  const update = (path: string, val: unknown) => {
    setCfg((prev: any) => {
      const next = { ...prev };
      const parts = path.split('.');
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) { obj[parts[i]!] = { ...obj[parts[i]!] }; obj = obj[parts[i]!]; }
      obj[parts[parts.length - 1]!] = val;
      return next;
    });
    setTestResult(null);
  };

  const handleSave = () => {
    saveConfig(cfg);
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
    onSaved?.(cfg);
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      await testConnection(cfg);
      setTestResult({ ok: true, msg: `✓ Connected to ${(PROVIDERS as any)[cfg.provider].name} (${cfg.models[cfg.provider]})` });
    } catch (e: any) {
      setTestResult({ ok: false, msg: e.message });
    }
    setTesting(false);
  };

  const activeP = (PROVIDERS as any)[cfg.provider];

  return (
    <div className="fade-in" style={{ maxWidth: 700 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.6 }}>
          Choose the preferred AI model provider and defaults for the consultation module. API keys live only in the backend's environment — every AI call is routed through the server, pseudonymized, and audit-logged.
        </div>
      </div>

      <Section title="Hospital AI processing policy">
        {policyLoading ? (
          <div style={{ fontSize: 13, color: '#9CA3AF' }}>Loading…</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: policy?.ai_processing_enabled ? '#ECFDF5' : '#FEF2F2', border: `1px solid ${policy?.ai_processing_enabled ? '#A7F3D0' : '#FECACA'}`, borderRadius: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: policy?.ai_processing_enabled ? '#065F46' : '#991B1B' }}>
                AI processing is {policy?.ai_processing_enabled ? 'enabled' : 'disabled'} for this hospital
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3 }}>
                When disabled, every AI drafting/extraction call (consultation notes, discharge summary, prescription drafting, voice field-fill) is blocked server-side across all branches.
                {policy?.updated_by && <> Last changed by {policy.updated_by}{policy.updated_at ? ` on ${new Date(policy.updated_at).toLocaleString()}` : ''}.</>}
              </div>
            </div>
            <button className="btn-ghost" onClick={togglePolicy} disabled={!!policySaving}>
              {policySaving === 'processing' ? 'Saving…' : policy?.ai_processing_enabled ? 'Disable AI processing' : 'Enable AI processing'}
            </button>
          </div>
        )}

        {!policyLoading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', marginTop: 10, background: policy?.ai_consent_required ? '#ECFDF5' : '#FFFBEB', border: `1px solid ${policy?.ai_consent_required ? '#A7F3D0' : '#FDE68A'}`, borderRadius: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: policy?.ai_consent_required ? '#065F46' : '#92400E' }}>
                Patient AI-processing consent is {policy?.ai_consent_required ? 'required' : 'not required'}
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3 }}>
                {policy?.ai_consent_required
                  ? 'A patient-linked AI call is blocked (AI_CONSENT_REQUIRED) until that patient has a granted consent recorded in Patient Records.'
                  : 'Patient-linked AI calls are no longer gated on a recorded consent — use this only if consent is captured another way (e.g. on paper at admission) or isn’t applicable to this deployment. This does not affect the master AI on/off switch above.'}
              </div>
            </div>
            <button className="btn-ghost" onClick={toggleConsentRequired} disabled={!!policySaving}>
              {policySaving === 'consent' ? 'Saving…' : policy?.ai_consent_required ? 'Make consent optional' : 'Require consent again'}
            </button>
          </div>
        )}
        {!policyLoading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', marginTop: 10, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>Default AI vendor for patient documents</div>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3 }}>
                Which provider handles consultation notes, discharge summaries, prescription drafting, and patient summary/timeline generation — the routes that send (pseudonymized) patient data. Enforced server-side; a browser can no longer redirect these to a different vendor. Leave unset to auto-use whichever provider has a configured API key.
              </div>
            </div>
            <select
              value={policy?.default_ai_provider || ''}
              onChange={(e) => setDefaultProvider(e.target.value)}
              disabled={!!policySaving}
              style={{ width: 160, flexShrink: 0 }}
            >
              <option value="">Auto-detect</option>
              {Object.values(PROVIDERS).map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}
        {!policyLoading && (
          <div className="mt-2.5 flex items-center gap-3.5 rounded-[10px] border border-border bg-muted p-3.5">
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-bold text-ink">Default AI vendor for voice</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                Speech-to-text dictation, translation, transliteration and kiosk voice guidance. Enforced
                server-side, same as the document vendor above.
              </div>
            </div>
            <select
              className="w-40 shrink-0"
              value={policy?.default_voice_provider || 'sarvam'}
              onChange={(e) => setDefaultVoiceProvider(e.target.value)}
              disabled={!!policySaving}
            >
              {VOICE_PROVIDERS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}
        {!policyLoading && (
          <div className={`mt-2.5 rounded-[10px] border p-3.5 ${policy?.kiosk_ai_extraction_enabled ? 'border-success bg-success-soft' : 'border-border bg-muted'}`}>
            <div className="flex items-center gap-3.5">
              <div className="min-w-0 flex-1">
                <div className={`text-[13px] font-bold ${policy?.kiosk_ai_extraction_enabled ? 'text-success-ink' : 'text-ink'}`}>
                  Kiosk AI extraction is {policy?.kiosk_ai_extraction_enabled ? 'on' : 'off'} (default off)
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  OpenAI is never used for kiosk check-in, regardless of this switch. Off: the kiosk's on-premise
                  parser extracts name/mobile/age/gender/DOB. On: the voice vendor's own LLM is tried first and the
                  parser fills any gap. Only turn this on after running the D23 accuracy script
                  (<code>npm run kiosk:accuracy</code>) against real role-play recordings.
                </div>
              </div>
              <button className="btn-ghost shrink-0" onClick={toggleKioskAiExtraction} disabled={!!policySaving}>
                {policySaving === 'kioskExtraction' ? 'Saving…' : policy?.kiosk_ai_extraction_enabled ? 'Turn off' : 'Turn on'}
              </button>
            </div>
            {policy?.kiosk_ai_extraction_enabled && (
              <div className="mt-3 flex items-end gap-2">
                <div className="flex-1">
                  <label className="lbl">Extraction model (leave blank for the vendor default)</label>
                  <input
                    className="w-full"
                    value={kioskModelDraft}
                    onChange={(e) => setKioskModelDraft(e.target.value)}
                    placeholder="e.g. sarvam-m"
                  />
                </div>
                <button className="btn-ghost shrink-0" onClick={saveKioskExtractionModel} disabled={!!policySaving}>
                  {policySaving === 'kioskModel' ? 'Saving…' : 'Save'}
                </button>
              </div>
            )}
          </div>
        )}
        {policyError && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#991B1B' }}>{policyError}</div>
        )}
      </Section>

      <Section title="Consultation recordings & clinical draft">
        {!policyLoading && (
          <>
            <div className="rounded-[10px] border border-border bg-muted p-3.5">
              <div className="text-[13px] font-bold text-ink">
                {(policy?.consultation_audio_retention_days ?? 0) > 0
                  ? `Consultation audio is kept for ${policy?.consultation_audio_retention_days} days`
                  : 'Consultation audio is not kept'}
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                The written transcript of every consultation recording is always saved with the visit. The audio itself is kept
                only for the number of days set here, then deleted automatically; 0 means audio is never stored. A shorter period
                applies to recordings made after the change.
              </div>
              <div className="mt-2.5 flex items-end gap-2">
                <div className="w-40">
                  <label className="lbl">Keep audio for (days)</label>
                  <input
                    type="number" min={0} max={3650} className="w-full"
                    value={retentionDraft ?? String(policy?.consultation_audio_retention_days ?? 0)}
                    onChange={(e) => setRetentionDraft(e.target.value)}
                  />
                </div>
                <button className="btn-ghost shrink-0" onClick={saveAudioRetention} disabled={!!policySaving || retentionDraft == null}>
                  {policySaving === 'audioRetention' ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
            <div className={`mt-2.5 flex items-center gap-3.5 rounded-[10px] border p-3.5 ${policy?.structured_clinical_draft ? 'border-success bg-success-soft' : 'border-border bg-muted'}`}>
              <div className="flex-1">
                <div className={`text-[13px] font-bold ${policy?.structured_clinical_draft ? 'text-success-ink' : 'text-ink'}`}>
                  Six-section clinical draft is {policy?.structured_clinical_draft ? 'on' : 'off'}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Consultation Notes are drafted under Chief Complaints, History, Examination, Assessment, Treatment Information and
                  Clinical Summary, and the doctor is told which of these a note is missing before approving. Off: notes follow the
                  hospital's own AI role profile.
                </div>
              </div>
              <button className="btn-ghost shrink-0" disabled={!!policySaving}
                onClick={() => patchPolicy('structuredDraft', { structured_clinical_draft: !policy?.structured_clinical_draft })}>
                {policySaving === 'structuredDraft' ? 'Saving…' : policy?.structured_clinical_draft ? 'Turn off' : 'Turn on'}
              </button>
            </div>
          </>
        )}
      </Section>

      <Section title="Doctor AI Memory">
        {!policyLoading && (
          <>
            <div className={`flex items-center gap-3.5 rounded-[10px] border p-3.5 ${policy?.doctor_memory_enabled ? 'border-success bg-success-soft' : 'border-border bg-muted'}`}>
              <div className="flex-1">
                <div className={`text-[13px] font-bold ${policy?.doctor_memory_enabled ? 'text-success-ink' : 'text-ink'}`}>
                  Doctor AI Memory is {policy?.doctor_memory_enabled ? 'on' : 'off'} for this hospital
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Lets each doctor set their own documentation preferences (length, wording, sections to include) that shape their own AI drafts. Doctor-approved only — nothing is learned or applied without the doctor's own say-so. A doctor can pause it for themselves from "My AI preferences" regardless of this switch.
                </div>
              </div>
              <button className="btn-ghost" onClick={toggleDoctorMemory} disabled={!!policySaving}>
                {policySaving === 'memory' ? 'Saving…' : policy?.doctor_memory_enabled ? 'Turn off' : 'Turn on'}
              </button>
            </div>
            <div className="mt-2.5 rounded-[10px] border border-border bg-muted p-3.5">
              <div className="mb-2 text-[13px] font-bold text-ink">Applies to</div>
              <div className="flex flex-wrap gap-3.5">
                {MEMORY_DOC_TYPE_OPTIONS.map((docType) => (
                  <label key={docType} className={`flex items-center gap-1.5 text-[13px] ${policySaving ? 'cursor-default' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={!!policy?.memory_doc_types?.includes(docType)}
                      disabled={!!policySaving}
                      onChange={() => toggleMemoryDocType(docType)}
                    />
                    {docType}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}
      </Section>

      <Section title="AI Provider (model defaults for this browser)">
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12 }}>
          This picks which provider's model/temperature/token settings to configure below and to use for "⚡ Test connection" — it does not control which vendor patient documents are sent to. See "Default AI vendor for patient documents" above for that.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 18 }}>
          {Object.values(PROVIDERS).map((p: any) => (
            <ProviderCard key={p.id} provider={p} selected={cfg.provider === p.id}
              onSelect={() => update('provider', p.id)} />
          ))}
        </div>
      </Section>

      <Section title={`${activeP.name} — configuration`}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label className="lbl">Model</label>
            <select value={cfg.models[cfg.provider]} onChange={(e) => update(`models.${cfg.provider}`, e.target.value)}>
              {activeP.models.map((m: any) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>

          <div>
            <label className="lbl">Temperature <span style={{ fontWeight: 400, textTransform: 'none' }}>({cfg.temperature} — lower = more deterministic)</span></label>
            <input type="range" min="0" max="1" step="0.1" value={cfg.temperature}
              onChange={(e) => update('temperature', parseFloat(e.target.value))}
              style={{ width: '100%', marginTop: 6 }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>
              <span>0 — Precise</span><span>0.5 — Balanced</span><span>1 — Creative</span>
            </div>
          </div>

          <div>
            <label className="lbl">Max output tokens</label>
            <select value={cfg.maxTokens} onChange={(e) => update('maxTokens', parseInt(e.target.value))}>
              {[512, 1000, 1500, 2000, 4000].map((n) => <option key={n} value={n}>{n.toLocaleString()} tokens</option>)}
            </select>
          </div>
        </div>
      </Section>

      <Section title="All providers">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {Object.values(PROVIDERS).map((p: any) => {
            const model = cfg.models[p.id] || p.defaultModel;
            return (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#F9FAFB', border: `1px solid ${cfg.provider === p.id ? p.color : '#E5E7EB'}`, borderRadius: 10 }}>
                <span style={{ fontSize: 20 }}>{p.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>{model}</div>
                </div>
                {cfg.provider === p.id && <span style={{ padding: '2px 8px', background: p.color, color: '#fff', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>Active</span>}
              </div>
            );
          })}
        </div>
      </Section>

      {testResult && (
        <div style={{ padding: '10px 14px', borderRadius: 10, marginBottom: 16, background: testResult.ok ? '#ECFDF5' : '#FEF2F2', border: `1px solid ${testResult.ok ? '#A7F3D0' : '#FECACA'}`, fontSize: 13, color: testResult.ok ? '#065F46' : '#991B1B', fontWeight: 500 }}>
          {testResult.msg}
        </div>
      )}

      <UsageSection />

      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="btn-primary" onClick={handleSave} style={{ minWidth: 120 }}>
          {saved ? '✓ Saved!' : 'Save settings'}
        </button>
        <button className="btn-ghost" onClick={handleTest} disabled={testing}>
          {testing ? 'Testing…' : '⚡ Test connection'}
        </button>
      </div>
    </div>
  );
}
